import { utils, WorkBook } from "xlsx";

import { InstrumentModelTypeEnum } from "../../../types.generated";

export interface DurationRiskRow {
    clientName: string;
    _id: string;
    name: string;
    modelType: string;
    currency: string;
    quantity: number;
    notional: number;
    value: number;
    isCashAccount: boolean;
    creditYieldDelta: number;
    interestRateYieldDelta: number;
    zeroSpread: number;
    [bucket: `bucket${number}`]: number;
}

type ExcelCell = string | number | null;

const creditModelTypes: string[] = [InstrumentModelTypeEnum.Bond, InstrumentModelTypeEnum.CdsIndex, InstrumentModelTypeEnum.CdsBasket];

/** Credit duration for one position. Bonds relative to market value, CDS relative to notional in base currency. */
export const getCreditDuration = (row: DurationRiskRow, fxRate: number): number => {
    if (!creditModelTypes.includes(row.modelType) || !(row.creditYieldDelta || row.creditYieldDelta === 0) || !row.value) {
        return null;
    }
    if (row.modelType === InstrumentModelTypeEnum.Bond) return (10000 * row.creditYieldDelta) / row.value;
    return (10000 * row.creditYieldDelta) / row.notional / fxRate;
};

/** Modified duration for one position. Only defined for bonds. */
export const getModifiedDuration = (row: DurationRiskRow): number => {
    if (row.modelType !== InstrumentModelTypeEnum.Bond || !(row.interestRateYieldDelta || row.interestRateYieldDelta === 0) || !row.value) {
        return null;
    }
    return (10000 * row.interestRateYieldDelta) / row.value;
};

export const RISK_DOCS_URL = "https://portal.captor.se/riskdocs";

const getSummaryRow = (
    label: string,
    rows: DurationRiskRow[],
    bucketLabels: string[],
    baseCurrency: string,
    showDurations: boolean
): Record<string, ExcelCell> => {
    const value = rows.reduce((acc, row) => acc + (row.value ?? 0), 0);
    const creditYieldDelta = rows.reduce((acc, row) => acc + (row.creditYieldDelta ?? 0), 0);
    const interestRateYieldDelta = rows.reduce((acc, row) => acc + (row.interestRateYieldDelta ?? 0), 0);
    const onlyCash = rows.every((row) => row.isCashAccount);
    const summaryRow: Record<string, ExcelCell> = {
        Group: label,
        "Number of positions": rows.length,
        [`Value (${baseCurrency})`]: value,
        "Credit Duration": showDurations && value ? (10000 * creditYieldDelta) / value : null,
        [`Credit Delta (${baseCurrency}/bp)`]: rows.some((row) => creditModelTypes.includes(row.modelType)) ? creditYieldDelta : null,
        "Modified Duration": showDurations && value ? (10000 * interestRateYieldDelta) / value : null,
        [`Yield Delta (${baseCurrency}/bp)`]: onlyCash ? null : interestRateYieldDelta
    };
    bucketLabels.forEach((bucketLabel, i) => {
        summaryRow[bucketLabel] = onlyCash ? null : rows.reduce((acc, row) => acc + (row[`bucket${i}`] ?? 0), 0);
    });
    return summaryRow;
};

export interface DurationRiskWorkbookInput {
    rows: DurationRiskRow[];
    bucketLabels: string[];
    fxDictionary: Record<string, number>;
    baseCurrency: string;
    clientName: string;
    endDate: string;
}

/**
 * Builds a workbook with a summary per instrument type, all positions (one row each) and an info sheet with units.
 * Values follow the on-screen table: amounts in base currency, zero spread in bps.
 */
export const buildDurationRiskWorkbook = ({
    rows,
    bucketLabels,
    fxDictionary,
    baseCurrency,
    clientName,
    endDate
}: DurationRiskWorkbookInput): WorkBook => {
    // Sorted by type ascending, then by value descending
    const sortedRows = [...rows].sort((a, b) => a.modelType.localeCompare(b.modelType) || b.value - a.value);
    const positions = sortedRows.map((row) => {
        const positionRow: Record<string, ExcelCell> = {
            Type: row.modelType,
            Name: row.name,
            Currency: row.currency,
            "Position (local)": row.quantity,
            "Notional (local)": row.notional,
            [`Value (${baseCurrency})`]: row.value,
            "Zero Spread (bp)":
                row.modelType === InstrumentModelTypeEnum.Bond && (row.zeroSpread || row.zeroSpread === 0) ? row.zeroSpread * 10000 : null,
            "Credit Duration": getCreditDuration(row, fxDictionary[row.currency + baseCurrency]),
            [`Credit Delta (${baseCurrency}/bp)`]:
                creditModelTypes.includes(row.modelType) && (row.creditYieldDelta || row.creditYieldDelta === 0)
                    ? row.creditYieldDelta
                    : null,
            "Modified Duration": getModifiedDuration(row),
            [`Yield Delta (${baseCurrency}/bp)`]: row.isCashAccount ? null : row.interestRateYieldDelta
        };
        bucketLabels.forEach((bucketLabel, i) => {
            positionRow[bucketLabel] = row.isCashAccount ? null : (row[`bucket${i}`] ?? null);
        });
        return positionRow;
    });

    const rowsByType: Record<string, DurationRiskRow[]> = {};
    for (const row of rows) {
        if (!rowsByType[row.modelType]) rowsByType[row.modelType] = [];
        rowsByType[row.modelType].push(row);
    }
    // Durations are only shown for the fund total and for bonds, same as the on-screen table
    const summary = [getSummaryRow("Total", rows, bucketLabels, baseCurrency, true)];
    for (const [modelType, typeRows] of Object.entries(rowsByType)) {
        summary.push(getSummaryRow(modelType, typeRows, bucketLabels, baseCurrency, modelType === InstrumentModelTypeEnum.Bond));
    }
    const summarySheet = utils.json_to_sheet(summary);
    // Documentation link below the table, after one empty row, so the table itself stays filterable
    const docsRowIndex = summary.length + 2;
    utils.sheet_add_aoa(summarySheet, [["How to interpret the data", RISK_DOCS_URL]], { origin: docsRowIndex });
    summarySheet[utils.encode_cell({ r: docsRowIndex, c: 1 })].l = { Target: RISK_DOCS_URL };

    const bucketRange = bucketLabels.length ? `${bucketLabels[0]}–${bucketLabels[bucketLabels.length - 1]}` : "Buckets";
    const infoSheet = utils.aoa_to_sheet([
        ["Client", clientName],
        ["End date", endDate],
        ["Base currency", baseCurrency],
        [],
        ["Column", "Unit"],
        ["Position (local)", "Local currency of the instrument (see Currency column). Number of contracts for swaps, swaptions and CDS"],
        ["Notional (local)", "Local currency of the instrument (see Currency column)"],
        [`Value (${baseCurrency})`, baseCurrency],
        [`Credit Delta, Yield Delta, ${bucketRange}`, `${baseCurrency} per 1 bp shift`],
        ["Zero Spread (bp)", "Basis points"],
        ["Credit Duration, Modified Duration", "Years"]
    ]);

    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, summarySheet, "Summary");
    utils.book_append_sheet(workbook, utils.json_to_sheet(positions), "Positions");
    utils.book_append_sheet(workbook, infoSheet, "Info");
    return workbook;
};
